import {HttpRequest} from '@services/HttpClient';
import {APIInterface, APIResponseInterface} from '@services/services.type';
import {trackErrorEvents} from '@utils/googleAnalytics/googleAnalyticsEvents';
import {getAPIUrl} from '@utils/service.utils';

const COUNTY_CODE_DELIMITER = '___';

export type GetLeadGenerationOptionsRO = LeadGenerationData;

type LeadGenerationData = {
  preferredLanguages: Array<Record<string, string>>;
  countries: Array<Record<'label' | 'value' | 'flagCode', string>>;
  countryCode: Array<Record<string, string>>;
  nationalities: Array<Record<string, string>>;
  interests: {value: string; label: string}[];
  cities: {[key: string]: any[]};
};

type GetLeadGenOptionsAPIRO = APIResponseInterface<{
  countries: {
    city: string[];
    country_code: string;
    country_flag_code: string;
    country_name: string;
    sf_country: string;
  }[];
  interests: Record<'value' | 'id', string>[];
  preferredLanguages: Record<'value', string>[];
}>;

export function getLeadGenerationOptions(): APIInterface<
  undefined,
  GetLeadGenerationOptionsRO
> {
  const route = 'properties/feature/lead-meta';
  const httpRequest = new HttpRequest(getAPIUrl('property', route));
  return {
    getResponse: async () => {
      try {
        const response = await httpRequest.send();
        return parseResponse(
          (response as unknown as GetLeadGenOptionsAPIRO).data
        );
      } catch (err: any) {
        trackErrorEvents({
          type: 'get_lead_generation_option_fetch_failed',
          errorStack: err,
        });
        throw err;
      }
    },
    abort: httpRequest.abort,
  };
}

const parseResponse = (
  data: GetLeadGenOptionsAPIRO['data']
): LeadGenerationData => {
  const sortedCountries = data?.countries?.sort((a, b) => {
    if (a.country_name < b.country_name) return -1;
    if (a.country_name > b.country_name) return 1;
    return 0;
  });

  let preferredLanguages = data?.preferredLanguages.map((item) => {
    return {
      label: item.value,
      value: item.value,
    };
  });

  let countries: Record<string, string>[] = sortedCountries.reduce(
    (acc: Record<string, string>[], item) => {
      if (acc.find(({label}) => label === item.country_name)) return acc;
      return [
        ...acc,
        {
          label: item.country_name,
          value: item.sf_country,
          flagCode: item.country_flag_code,
        },
      ];
    },
    []
  );

  let countryCode = sortedCountries.map((item) => ({
    label: `(${item.country_code || 'NA'}) ${item.country_name}`,
    value: getValueStrFromCountyCodeAndValue(
      item.country_code,
      item.sf_country
    ),
    flagCode: item.country_flag_code,
    code: item.country_code,
    name: item.sf_country,
  }));
  let nationalities = [...countries];
  let cities: any = {};
  let updatedCities = sortedCountries.map((item) => {
    cities[item.country_name] = item.city;
    return cities;
  });
  updatedCities = cities;

  let interests = data.interests?.map((item) => {
    return {
      label: item.value,
      value: item.value,
    };
  });
  return {
    countryCode,
    countries,
    preferredLanguages,
    nationalities,
    cities,
    interests,
  };
};

export function getValueStrFromCountyCodeAndValue(code: string, name: string) {
  return `${code}${COUNTY_CODE_DELIMITER}${name}`;
}
export function getCountryCodeAndNameFromValue(value: string) {
  const [code, name] = value.split(COUNTY_CODE_DELIMITER);
  return {code, name};
}
